import { useEffect, useState } from "react";
import { Building2, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inputCls = "h-10 bg-[#0F0F12] border-[#1A1A22] focus:border-blue-500 focus:ring-1 focus:ring-blue-500";
const labelCls = "text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400";

export default function Branches() {
  const { user } = useAuth();
  const [branches, setBranches] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/branches").then((r) => setBranches(r.data));
  useEffect(() => { load(); }, []);

  const openDialog = (b) => {
    setEditing(b || null);
    setForm(b ? { name: b.name, address: b.address || "", phone: b.phone || "" } : { name: "", address: "", phone: "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/branches/${editing.id}`, form);
        toast.success("Cabang diperbarui");
      } else {
        await api.post("/branches", form);
        toast.success("Cabang ditambahkan");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus cabang ini?")) return;
    try {
      await api.delete(`/branches/${id}`);
      toast.success("Cabang dihapus");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="branches-page">
      <PageHeader title="Cabang" subtitle="Kelola lokasi showroom / cabang">
        {user?.role === "admin" && (
          <Button data-testid="add-branch-button" onClick={() => openDialog(null)} className="bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold rounded-md">
            <Plus className="w-4 h-4 mr-1" /> Tambah Cabang
          </Button>
        )}
      </PageHeader>

      {!branches ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-32 bg-[#0A0A0C] border border-[#1A1A22] rounded-md animate-pulse" />)}
        </div>
      ) : branches.length === 0 ? (
        <EmptyState icon={Building2} title="Belum ada cabang" desc="Tambahkan cabang pertama Anda" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {branches.map((b, i) => (
            <div key={b.id} data-testid={`branch-card-${b.id}`} className="fade-up bg-[#0A0A0C] border border-[#1A1A22] rounded-md p-5 hover:-translate-y-[2px] transition-transform duration-200" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-9 h-9 rounded-md bg-[#121218] border border-[#26262E] flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-blue-500" />
                </div>
                {user?.role === "admin" && (
                  <div className="flex gap-1">
                    <button data-testid={`edit-branch-${b.id}`} onClick={() => openDialog(b)} className="p-1.5 text-zinc-500 hover:text-zinc-100 transition-colors duration-200"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`delete-branch-${b.id}`} onClick={() => remove(b.id)} className="p-1.5 text-zinc-500 hover:text-rose-500 transition-colors duration-200"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>
              <p className="font-heading font-bold text-zinc-50">{b.name}</p>
              <p className="text-sm text-zinc-500 mt-1">{b.address || "Alamat belum diisi"}</p>
              {b.phone && <p className="text-xs text-zinc-500 mt-1 font-mono">{b.phone}</p>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold">{editing ? "Edit Cabang" : "Tambah Cabang"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="branch-form">
            <div className="space-y-2">
              <Label className={labelCls}>Nama Cabang</Label>
              <Input data-testid="branch-name-input" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Cabang Bekasi" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Alamat</Label>
              <Input data-testid="branch-address-input" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} placeholder="Jl. Raya No. 1" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Telepon</Label>
              <Input data-testid="branch-phone-input" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="0812xxxxxxx" className={inputCls} />
            </div>
            <Button data-testid="branch-submit-button" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
